import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { useSnackbar } from "notistack";
import useActions, { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectLibrary, setError } from "../../library/librarySlice";
import { Automation } from "../../../types/Automation";
import {
  selectCodeSelection,
  selectEditingDash,
  selectViewCodeState,
} from "../../editor/dashEditorSlice";

export default function RunAutomationDialog(props: {
  parentCloseHandle: () => void;
}) {
  const actions = useActions();
  const dispatch = useAppDispatch();
  const librarySlice = useAppSelector(selectLibrary);
  const dash = useAppSelector(selectEditingDash);
  const viewCodeState = useAppSelector(selectViewCodeState);
  const codeSelection = useAppSelector(selectCodeSelection);
  const { enqueueSnackbar } = useSnackbar();
  const [runningAutomationId, setRunningAutomationId] = useState<number | null>(null);

  // Grab the current automations for the workspace
  useEffect(() => {
    actions.getAutomations();
  }, []);

  // When automation request finishes (status idle): on failure show toast and re-enable button; on success close dialog
  useEffect(() => {
    if (runningAutomationId === null || librarySlice.status !== "idle") return;
    if (librarySlice.error) {
      enqueueSnackbar(librarySlice.error, { variant: "error" });
      setRunningAutomationId(null);
      dispatch(setError(undefined));
    } else {
      setTimeout(() => {
        setRunningAutomationId(null);
        props.parentCloseHandle();
      }, 100);
    }
  }, [librarySlice.status, librarySlice.error, runningAutomationId, enqueueSnackbar, dispatch]);

  function handleSelectAutomation(automation: Automation) {
    if (automation.provides_code) {
      if (!dash || viewCodeState !== 3) {
        enqueueSnackbar(
          "To run this automation, open a dash in CURRENT CODE view and select some code.",
          { variant: "warning" }
        );
        return;
      }

      if (!codeSelection || codeSelection.trim() === "") {
        enqueueSnackbar(
          "Select some code in the editor before running this automation.",
          { variant: "warning" }
        );
        return;
      }
    }

    setRunningAutomationId(automation.id);
    const codeFileName = dash?.repo_file_name ?? dash?.name ?? "";
    actions.runAutomation({
      automationId: automation.id,
      selection: automation.provides_code ? codeSelection || "" : "",
      code: codeFileName,
    });
  }

  const isLoading = runningAutomationId !== null && librarySlice.status === "loading";

  return (
    <Dialog open={true} onClose={() => {
      if (!isLoading) {
        props.parentCloseHandle();
      }
    }}>
      <DialogTitle sx={{ position: "relative" }}>
        <Typography sx={{marginBottom: "20px"}}>Run Automation</Typography>
        {isLoading && (
          <CircularProgress 
            size={24} 
            sx={{ 
              position: "absolute",
              top: 16,
              right: 16
            }} 
          />
        )}
      </DialogTitle>
      
      <DialogContent>
        <Box component="form" sx={{ p: 2, m: 1 }}>
          { librarySlice.automations.length == 0 && <Typography sx={{marginBottom: "20px"}}>No automations in the workspace...</Typography> }

          <List>
              { librarySlice.automations.map( _ => <ListItem 
                key={'automation_' + _.id}
                button
                disabled={isLoading}
                onClick={() => handleSelectAutomation(_)}> 
              <ListItemText primary={<div>{ _.name }</div>}></ListItemText>
            </ListItem> )}
          </List>

        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={() => {
            if (!isLoading) {
              props.parentCloseHandle();
            }
          }}
          disabled={isLoading}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
